/**
 * Who sees which destination. Arcanum 2.0, 03_NAVIGATION_AND_PERMISSIONS.
 *
 * One pure function, deliberately separate from any component, because this is
 * where a permission mistake would live and a pure function can be tested
 * without a browser. The shell (Phase 3) and the staff surfaces (Phase 5)
 * render whatever this returns and add no rules of their own.
 *
 * THIS IS NOT AUTHORISATION. A hidden menu item protects nothing; every route
 * and every API decides for itself on the server. What this does is keep the
 * interface from offering somebody a door they cannot use, and -- for The
 * Sworn -- from telling anybody outside the circle that the circle exists.
 *
 * Rules that are easy to break later, stated once:
 *
 *   - Exactly five primary items for members and for the Founder. Operators
 *     get four: Desk is built on /stats, which is superadmin-only.
 *   - The Sworn appears ONLY for a user whose own session says is_sworn. No
 *     placeholder, no locked item, no gap for anybody else. The server never
 *     sends is_sworn to a caller outside the circle, so an absent flag is
 *     treated exactly like false.
 *   - Operators never see Registry, Operators, Assignments, Broadcast, Desk or
 *     Last Resort -- not disabled, absent. Their Members list is read-only and
 *     scoped to their assignments by the server.
 *   - Both staff roles keep a Society group: The Commons and their OWN vault,
 *     which they reach today from the account menu. Taking that away was the
 *     first conflict found in review.
 */

const isSworn = (user) => user?.is_sworn === true;

const MEMBER_PRIMARY = [
  { key: 'room', label: 'Room', to: '/room' },
  { key: 'commons', label: 'The Commons', to: '/commons' },
  { key: 'support', label: 'Support', to: '/dashboard' },
  { key: 'vault', label: 'Vault', to: '/vault' },
  { key: 'more', label: 'More', to: '/settings' },
];

const FOUNDER_PRIMARY = [
  { key: 'desk', label: 'Desk', to: '/admin/desk' },
  { key: 'queue', label: 'Queue', to: '/dashboard' },
  { key: 'members', label: 'Members', to: '/admin/members' },
  { key: 'invites', label: 'Invites', to: '/admin/invites' },
  { key: 'more', label: 'More', to: '/admin/more' },
];

const OPERATOR_PRIMARY = FOUNDER_PRIMARY.filter((i) => i.key !== 'desk');

/** The Society group both staff roles keep. */
const society = (user) => ({
  key: 'society',
  label: 'Society',
  items: [
    { key: 'commons', label: 'The Commons', to: '/commons' },
    { key: 'vault', label: 'Private Vault', to: '/vault' },
    ...(isSworn(user) ? [{ key: 'sworn', label: 'The Sworn', to: '/sworn' }] : []),
  ],
});

/**
 * @returns {{ role: string, primary: Array, more: Array, roomExtras: Array }}
 *   `primary` is the bottom navigation, `more` the grouped entries behind
 *   More, `roomExtras` the access points Room adds beyond its shared four.
 */
export function navFor(user) {
  const role = user?.role;

  if (role === 'superadmin') {
    return {
      role,
      primary: FOUNDER_PRIMARY,
      roomExtras: [],
      more: [
        {
          key: 'operations',
          label: 'Operations',
          items: [
            { key: 'operators', label: 'Operators', to: '/admin/more/operators' },
            { key: 'assignments', label: 'Assignments', to: '/admin/more/assignments' },
            { key: 'broadcast', label: 'Broadcast', to: '/admin/more/broadcast' },
          ],
        },
        {
          key: 'governance',
          label: 'Governance',
          items: [
            { key: 'audit', label: 'Audit Log', to: '/admin/more/audit' },
            { key: 'last-resort', label: 'Last Resort', to: '/admin/more/last-resort' },
          ],
        },
        society(user),
        { key: 'account', label: 'Account & Application', items: [] },
      ],
    };
  }

  if (role === 'operator') {
    return {
      role,
      primary: OPERATOR_PRIMARY,
      roomExtras: [],
      more: [
        // Their OWN entries: the server filters the audit log by caller.
        {
          key: 'governance',
          label: 'Governance',
          items: [{ key: 'audit', label: 'Audit Log', to: '/admin/more/audit' }],
        },
        society(user),
        { key: 'account', label: 'Account & Application', items: [] },
      ],
    };
  }

  // Everyone else is a member. An unknown role gets the member's view, which
  // is the least privileged one there is -- never a staff view by accident.
  return {
    role: 'user',
    primary: MEMBER_PRIMARY,
    roomExtras: isSworn(user) ? [{ key: 'sworn', label: 'The Sworn', to: '/sworn' }] : [],
    more: [
      { key: 'account', label: 'Account', items: [] },
      { key: 'security', label: 'Security', items: [] },
      {
        key: 'membership',
        label: 'Membership',
        items: isSworn(user) ? [{ key: 'sworn', label: 'The Sworn', to: '/sworn' }] : [],
      },
      { key: 'application', label: 'Application', items: [] },
    ],
  };
}

/** Every destination a user can reach from the navigation, flattened. */
export function reachableFor(user) {
  const n = navFor(user);
  return [
    ...n.primary,
    ...n.roomExtras,
    ...n.more.flatMap((g) => g.items),
  ];
}

/**
 * Where a signed-in user lands: Room for members, Desk for the Founder, the
 * queue for operators (they have no Desk -- it is built on superadmin-only
 * statistics).
 */
export function homeFor(user) {
  const role = navFor(user).role;
  if (role === 'superadmin') return '/admin/desk';
  if (role === 'operator') return '/dashboard';
  return '/room';
}

/** Whether `key` is one of this user's bottom-navigation destinations. */
export function isPrimary(user, key) {
  return navFor(user).primary.some((i) => i.key === key);
}

/** Where this user's More lives (/settings for members, /admin/more for staff). */
export function moreFor(user) {
  return navFor(user).primary.find((i) => i.key === 'more').to;
}

/**
 * Whether the navigation offers this user `path` -- used to guard staff
 * routes so a typed URL shows the same doors the menus do. Still not
 * authorisation: the API decides, and refuses, on its own.
 */
export function canReach(user, path) {
  // Exact, or beneath a destination -- but never beneath More itself: its
  // children are listed one by one, and reaching More must not mean reaching
  // every page that happens to live under its path (an operator reaches
  // /admin/more, not /admin/more/operators).
  return reachableFor(user).some((i) => path === i.to
    || (i.key !== 'more' && path.startsWith(`${i.to}/`)));
}

/**
 * Where the Sworn hangs in this user's navigation. It is never a primary
 * destination: members reach it through Room's fifth door, staff through
 * their Society group. That parent stays lit, and is where Back goes.
 */
export function swornParent(user) {
  return navFor(user).role === 'user'
    ? { key: 'room', to: '/room' }
    : { key: 'more', to: '/admin/more' };
}
